import apiV2, { getFilenameFromContentDisposition } from "api-v2";
import { showToast } from "utils/helpers";
import type {
	Clarification,
	ClarificationType,
	CreateClarificationPayload,
	UpdateClarificationPayload,
	SendClarificationsPayload,
	ImportClarificationsResult,
	ValidateImportResult,
	ImportClarificationRow,
	SubmitClarificationResponsePayload,
	SendResponsesPayload,
} from "../types/clarification.type";

interface ApiResponse<T> {
	data: T;
	message?: string;
	status: boolean;
}

export const getClarificationsBySubmissionId = async (
	rfxSubmissionId: number
): Promise<Clarification[]> => {
	try {
		const response = await apiV2.get<ApiResponse<Clarification[]>>(
			`rfx-submissions/${rfxSubmissionId}/clarifications`
		);
		return response.data?.status ? response.data.data : [];
	} catch (error) {
		console.error("Failed to fetch clarifications by submission:", error);
		return [];
	}
};

export const getClarificationsByRfxId = async (
	rfxId: number
): Promise<Clarification[]> => {
	try {
		const response = await apiV2.get<ApiResponse<Clarification[]>>(
			`rfx/${rfxId}/clarifications`
		);
		return response.data?.status ? response.data.data : [];
	} catch (error) {
		console.error("Failed to fetch clarifications by RFx:", error);
		return [];
	}
};

export const getClarificationTypes = async (): Promise<ClarificationType[]> => {
	try {
		const response =
			await apiV2.get<ApiResponse<ClarificationType[]>>(`clarification-types`);
		return response.data?.status ? response.data.data : [];
	} catch (error) {
		console.error("Failed to fetch clarification types:", error);
		return [];
	}
};

export const createClarification = async (
	rfxSubmissionId: number,
	payload: CreateClarificationPayload
): Promise<Clarification | null> => {
	try {
		const response = await apiV2.post<ApiResponse<Clarification>>(
			`rfx-submissions/${rfxSubmissionId}/clarifications`,
			payload
		);
		if (response.data?.status) {
			showToast("Clarification sent successfully", "Success", true);
			return response.data.data;
		}
		showToast(
			response.data?.message || "Failed to create clarification",
			"Error",
			false
		);
		return null;
	} catch (error) {
		console.error("Failed to create clarification:", error);
		showToast("Failed to create clarification", "Error", false);
		return null;
	}
};

export const respondToClarification = async (
	clarificationId: number,
	payload: SubmitClarificationResponsePayload
): Promise<Clarification | null> => {
	try {
		const response = await apiV2.patch<ApiResponse<Clarification>>(
			`clarifications/${clarificationId}/response`,
			payload
		);
		if (response.data?.status) {
			showToast("Clarification responded to successfully", "Success", true);
			return response.data.data;
		}
		showToast(
			response.data?.message || "Failed to respond to clarification",
			"Error",
			false
		);
		return null;
	} catch (error) {
		console.error("Failed to respond to clarification:", error);
		showToast("Failed to respond to clarification", "Error", false);
		return null;
	}
};

export const saveDraftResponse = async (
	clarificationId: number,
	payload: SubmitClarificationResponsePayload
): Promise<Clarification | null> => {
	try {
		const response = await apiV2.patch<ApiResponse<Clarification>>(
			`clarifications/${clarificationId}/draft-response`,
			payload
		);
		if (response.data?.status) {
			showToast("Draft response saved successfully", "Success", true);
			return response.data.data;
		}
		showToast(
			response.data?.message || "Failed to save draft response",
			"Error",
			false
		);
		return null;
	} catch (error) {
		console.error("Failed to save draft response:", error);
		showToast("Failed to save draft response", "Error", false);
		return null;
	}
};

export const sendResponses = async (
	payload: SendResponsesPayload
): Promise<Clarification[] | null> => {
	try {
		const response = await apiV2.post<ApiResponse<Clarification[]>>(
			`clarifications/send-responses`,
			payload
		);
		if (response.data?.status) {
			showToast(`Responses sent to client successfully`, "Success", true);
			return response.data.data;
		}
		showToast(
			response.data?.message || "Failed to send responses",
			"Error",
			false
		);
		return null;
	} catch (error) {
		console.error("Failed to send responses:", error);
		showToast("Failed to send responses", "Error", false);
		return null;
	}
};

export const updateClarification = async (
	clarificationId: number,
	payload: UpdateClarificationPayload
): Promise<Clarification | null> => {
	try {
		const response = await apiV2.patch<ApiResponse<Clarification>>(
			`clarifications/${clarificationId}`,
			payload
		);
		if (response.data?.status) {
			showToast("Clarification updated successfully", "Success", true);
			return response.data.data;
		}
		showToast(
			response.data?.message || "Failed to update clarification",
			"Error",
			false
		);
		return null;
	} catch (error) {
		console.error("Failed to update clarification:", error);
		showToast("Failed to update clarification", "Error", false);
		return null;
	}
};

export const deleteClarification = async (
	clarificationId: number
): Promise<boolean> => {
	try {
		const response = await apiV2.delete<ApiResponse<boolean>>(
			`clarifications/${clarificationId}`
		);
		if (response.data?.status) {
			showToast("Clarification deleted successfully", "Success", true);
			return true;
		}
		showToast(
			response.data?.message || "Failed to delete clarification",
			"Error",
			false
		);
		return false;
	} catch (error) {
		console.error("Failed to delete clarification:", error);
		showToast("Failed to delete clarification", "Error", false);
		return false;
	}
};

export const sendClarificationsBatch = async (
	rfxId: number,
	payload: SendClarificationsPayload
): Promise<boolean | null> => {
	try {
		const response = await apiV2.post<ApiResponse<Clarification[]>>(
			`rfx/${rfxId}/clarifications/send-batch`,
			payload
		);
		if (response.data?.status) {
			const count = payload.clarification_ids.length;
			showToast(
				`${count} clarification${count === 1 ? "" : "s"} sent (due: ${new Date(payload.date_due).toLocaleDateString()})`,
				"Success",
				true
			);
			return true;
		}
		showToast(
			response.data?.message || "Failed to send clarifications",
			"Error",
			false
		);
		return false;
	} catch (error) {
		console.error("Failed to send clarifications (batch):", error);
		showToast("Failed to send clarifications", "Error", false);
		return false;
	}
};

export const sendClarifications = async (
	rfxSubmissionId: number,
	payload: SendClarificationsPayload
): Promise<boolean> => {
	try {
		const response = await apiV2.post<ApiResponse<Clarification[]>>(
			`rfx-submissions/${rfxSubmissionId}/clarifications/send`,
			payload
		);
		if (response.data?.status) {
			showToast(
				`Clarifications sent to supplier successfully (due: ${new Date(payload.date_due).toLocaleDateString()})`,
				"Success",
				true
			);
			return true;
		}
		showToast(
			response.data?.message || "Failed to send clarifications",
			"Error",
			false
		);
		return false;
	} catch (error) {
		console.error("Failed to send clarifications:", error);
		showToast("Failed to send clarifications", "Error", false);
		return false;
	}
};

export const exportClarifications = async (
	rfxSubmissionId: number
): Promise<{ blob: Blob | null; filename: string }> => {
	try {
		const response = await apiV2.get(
			`rfx-submissions/${rfxSubmissionId}/clarifications/export`,
			{ responseType: "blob" }
		);

		const filename = getFilenameFromContentDisposition(response);

		return {
			blob: response.data,
			filename,
		};
	} catch (error) {
		console.error("Failed to export clarifications:", error);
		showToast("Failed to export clarifications", "Error", false);
		return null;
	}
};

export const downloadImportTemplate = async (): Promise<Blob | null> => {
	try {
		const response = await apiV2.get(`clarifications/import-template`, {
			responseType: "blob",
		});
		return response.data;
	} catch (error) {
		console.error("Failed to download import template:", error);
		showToast("Failed to download import template", "Error", false);
		return null;
	}
};

export const validateImportClarifications = async (
	rfxSubmissionId: number,
	file: File
): Promise<ValidateImportResult | null> => {
	try {
		const formData = new FormData();
		formData.append("file", file);

		const response = await apiV2.post<ApiResponse<ValidateImportResult>>(
			`rfx-submissions/${rfxSubmissionId}/clarifications/import/validate`,
			formData
		);

		if (response.data?.status) {
			return response.data.data;
		}
		showToast(
			response.data?.message || "Failed to validate import",
			"Error",
			false
		);
		return null;
	} catch (error) {
		console.error("Failed to validate import:", error);
		showToast("Failed to validate import file", "Error", false);
		return null;
	}
};

export const importClarifications = async (
	rfxSubmissionId: number,
	rows: ImportClarificationRow[]
): Promise<ImportClarificationsResult | null> => {
	try {
		const response = await apiV2.post<ApiResponse<ImportClarificationsResult>>(
			`rfx-submissions/${rfxSubmissionId}/clarifications/import`,
			{ rows }
		);
		if (response.data?.status) {
			const result = response.data.data;
			if (result.errors.length === 0) {
				showToast(
					`Successfully imported ${result.imported_count} clarifications`,
					"Success",
					true
				);
			}
			return result;
		}
		showToast(
			response.data?.message || "Failed to import clarifications",
			"Error",
			false
		);
		return null;
	} catch (error) {
		console.error("Failed to import clarifications:", error);
		showToast("Failed to import clarifications", "Error", false);
		return null;
	}
};
